import React, { useState, useRef, useEffect } from "react";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { format, isValid, parse } from "date-fns";
import { createPortal } from "react-dom";

interface DatePickerProps {
  value: string;
  onChange: (date: string) => void;
  disabled?: boolean;
  placeholder?: string;
  minYear?: number;
  maxYear?: number;
}

const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  disabled,
  placeholder = "Select date",
  minYear,
  maxYear,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [inputValue, setInputValue] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Calculate year range
  const currentYear = new Date().getFullYear();
  const startYear = minYear ?? currentYear - 100;
  const endYear = maxYear ?? currentYear;

  // Initialize input value from prop
  useEffect(() => {
    if (value) {
      const date = new Date(value);
      if (isValid(date)) {
        setInputValue(format(date, "MM/dd/yyyy"));
        setCurrentMonth(date);
      }
    } else {
      setInputValue("");
    }
  }, [value]);

  // Calculate dropdown position
  useEffect(() => {
    if (isOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropdownHeight = 380; // Approximate height of dropdown

      setDropdownPosition({
        top:
          spaceBelow >= dropdownHeight
            ? rect.bottom + 8
            : rect.top - dropdownHeight - 8,
        left: rect.left,
        width: Math.max(rect.width, 288), // Min width 288px (w-72)
      });
    }
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("scroll", handleScroll, true);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const selectedDate = value ? new Date(value) : null;

  // Get days in month
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days: (number | null)[] = [];

    // Add empty slots for days before the first day
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }

    // Add the days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    return days;
  };

  const handlePrevMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
    );
  };

  const handleNextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
    );
  };

  const handleYearChange = (year: number) => {
    setCurrentMonth(new Date(year, currentMonth.getMonth(), 1));
  };

  const handleMonthChange = (month: number) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), month, 1));
  };

  const handleDayClick = (day: number) => {
    const newDate = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    );
    onChange(newDate.toISOString());
    setInputValue(format(newDate, "MM/dd/yyyy"));
    setIsOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);

    // Try to parse the date
    const parsed = parse(val, "MM/dd/yyyy", new Date());
    if (isValid(parsed) && parsed.getFullYear() > 1900) {
      onChange(parsed.toISOString());
      setCurrentMonth(parsed);
    }
  };

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  const isSelectedDay = (day: number) => {
    if (!selectedDate) return false;
    return (
      selectedDate.getDate() === day &&
      selectedDate.getMonth() === currentMonth.getMonth() &&
      selectedDate.getFullYear() === currentMonth.getFullYear()
    );
  };

  const isToday = (day: number) => {
    const today = new Date();
    return (
      today.getDate() === day &&
      today.getMonth() === currentMonth.getMonth() &&
      today.getFullYear() === currentMonth.getFullYear()
    );
  };

  // Generate year options
  const years = Array.from(
    { length: endYear - startYear + 1 },
    (_, i) => endYear - i
  );

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const weekDays = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

  // Calendar Dropdown Content
  const dropdownContent = (
    <div
      ref={containerRef}
      className="fixed z-[9999] bg-white rounded-xl border border-gray-200 shadow-2xl p-4"
      style={{
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
        minWidth: "288px",
      }}
    >
      {/* Month/Year Selectors */}
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={handlePrevMonth}
          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>

        <div className="flex items-center gap-2">
          <select
            value={currentMonth.getMonth()}
            onChange={(e) => handleMonthChange(parseInt(e.target.value))}
            className="px-2 py-1 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-[#8B52FF] cursor-pointer"
          >
            {months.map((month, index) => (
              <option key={month} value={index}>
                {month}
              </option>
            ))}
          </select>

          <select
            value={currentMonth.getFullYear()}
            onChange={(e) => handleYearChange(parseInt(e.target.value))}
            className="px-2 py-1 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-[#8B52FF] cursor-pointer"
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>

        <button
          type="button"
          onClick={handleNextMonth}
          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ChevronRight className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      {/* Week Days Header */}
      <div className="grid grid-cols-7 mb-2">
        {weekDays.map((day) => (
          <div
            key={day}
            className="text-center text-xs font-medium text-gray-500 py-1"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Days Grid */}
      <div className="grid grid-cols-7 gap-1">
        {getDaysInMonth(currentMonth).map((day, index) => (
          <div key={index} className="aspect-square">
            {day !== null ? (
              <button
                type="button"
                onClick={() => handleDayClick(day)}
                className={`w-full h-full flex items-center justify-center text-sm rounded-lg transition-colors
                  ${
                    isSelectedDay(day)
                      ? "bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white font-medium"
                      : isToday(day)
                        ? "bg-gray-100 text-gray-900 font-medium"
                        : "text-gray-700 hover:bg-gray-100"
                  }
                `}
              >
                {day}
              </button>
            ) : (
              <div />
            )}
          </div>
        ))}
      </div>

      {/* Today Button */}
      <div className="mt-3 pt-3 border-t border-gray-100">
        <button
          type="button"
          onClick={() => {
            const today = new Date();
            setCurrentMonth(today);
          }}
          className="w-full py-2 text-sm font-medium text-[#8B52FF] hover:bg-[#8B52FF]/5 rounded-lg transition-colors"
        >
          Go to Today
        </button>
      </div>
    </div>
  );

  return (
    <div className="relative">
      {/* Input */}
      <div className="relative">
        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onClick={handleInputClick}
          disabled={disabled}
          placeholder={placeholder}
          readOnly
          className={`w-full pl-11 pr-4 py-3 rounded-xl border-2 transition-all duration-200
            ${
              !disabled
                ? "border-gray-200 focus:border-[#8B52FF] focus:ring-2 focus:ring-[#8B52FF]/20 bg-white cursor-pointer"
                : "border-transparent bg-gray-50 text-gray-700 cursor-not-allowed opacity-60"
            }
            focus:outline-none`}
        />
      </div>

      {/* Calendar Dropdown - Rendered via Portal */}
      {isOpen && !disabled && createPortal(dropdownContent, document.body)}
    </div>
  );
};

export default DatePicker;
